import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { ApolloProvider } from '@apollo/client'
import { t } from 'src/translations'
import { useEditor } from '@toasttab/sites-components'
import isEmail from 'validator/es/lib/isEmail'

import {
  GiftCardPurchaseSource,
  GiftCardSendType,
  useCreateGiftCardOrderMutation
} from 'src/apollo/onlineOrdering'
import { ButtonType } from 'src/apollo/sites'
import Image from 'src/shared/components/common/Image'
import { BrandedButton } from 'src/shared/components/common/button'
import InputField from 'src/shared/components/common/form_input/InputField'
import TextArea from 'src/shared/components/common/form_input/TextArea'
import { useOOClient } from 'src/shared/components/common/oo_client_provider/OOClientProvider'
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'
import { alertError } from 'src/shared/js/alertUtils'
import useTracker from 'src/lib/js/hooks/useTracker'
import { useCurrencySymbol } from 'src/lib/js/hooks/useFormatPrice'
import { useIsIntlRestaurant } from 'src/lib/js/hooks/useIsIntlRestaurant'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'

import CreditCardFormSPI from 'public/components/default_template/online_ordering/checkout/payment/CreditCardFormSPI'
import AdyenGiftCardPaymentIFrame from './AdyenGiftCardPaymentIFrame'
import { CartContextProvider } from 'public/components/online_ordering/CartContext'
import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'
import {
  PaymentContextProvider,
  usePayment
} from 'public/components/online_ordering/PaymentContext'

import config from 'config'

const AMOUNT_REGEX = new RegExp(/^\d{1,3}(?:\.\d{1,2})?$/)
const DEFAULT_MIN_PURCHASE_AMOUNT = 1
const DEFAULT_MAX_PURCHASE_AMOUNT = 500

type Props = {
  maxPurchaseAmount?: number
  minPurchaseAmount?: number
}

const GiftCardPayment = ({
  onPaymentIntentError
}: {
  onPaymentIntentError: () => void
}) => {
  const {
    isPaymentValid,
    completeGiftCardPayment,
    giftCardPaymentIntentError
  } = usePayment()
  const { ooRestaurant, selectedLocation } = useRestaurant()
  const isIntlRestaurant = useIsIntlRestaurant()
  const [createGiftCardOrder] = useCreateGiftCardOrderMutation()
  const { getValues } = useFormContext()
  const [complete, setComplete] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const tracker = useTracker()

  useEffect(() => {
    if (giftCardPaymentIntentError) {
      onPaymentIntentError()
    }
  }, [giftCardPaymentIntentError, onPaymentIntentError])

  const currency = useMemo(
    () => ooRestaurant?.i18n?.currency || 'USD',
    [ooRestaurant]
  )
  const value = parseInt(parseFloat(getValues()['amount']) * 100 + '')
  const onPaymentCompleted = useCallback(
    async (paymentId: string) => {
      grecaptcha.enterprise.ready(async () => {
        const token = await grecaptcha.enterprise.execute(
          config.giftCardRecaptchaSiteKey,
          { action: 'EGiftcard_PlaceOrder' }
        )

        try {
          const resp = await createGiftCardOrder({
            variables: {
              input: {
                restaurantGuid: selectedLocation.externalId,
                purchaseSource: GiftCardPurchaseSource.Websites,
                recaptchaToken: token,
                orderDetails: {
                  paymentGuid: paymentId,
                  totalAmount: { currency, value },
                  sender: {
                    email: getValues()['senderEmail'],
                    from: getValues()['senderName']
                  },
                  giftCards: [
                    {
                      amount: { currency, value },
                      recipient: {
                        email: getValues()['recipientEmail'],
                        to: getValues()['recipientName']
                      },
                      sendType: GiftCardSendType.Email,
                      message: getValues()['message']
                    }
                  ]
                }
              }
            }
          })

          if (
            resp?.data?.createGiftCardOrder.__typename ===
            'CreateGiftCardOrderResponse'
          ) {
            tracker.track(TrackingEvents.GIFT_CARD_PURCHASED, {
              // Convert from minor currency units (cents) to major units (dollars).
              // ASSUMPTION: This assumes all currencies use 100 minor units per major unit,
              // This is NOT true for all currencies and will need to be updated when expanding international support.
              amount: value / 100,
              currency_code: currency,
              conversion_id: resp?.data?.createGiftCardOrder?.orderGuid ?? null,
              restaurant_guid: selectedLocation.externalId
            })
            setComplete(true)
          } else {
            alertError(t('gift-card-purchase-form.order-failed'))
          }
        } catch (e) {
          alertError(t('gift-card-purchase-form.payment-failed'))
        } finally {
          setSubmitting(false)
        }
      })
    },
    [
      createGiftCardOrder,
      currency,
      getValues,
      selectedLocation.externalId,
      tracker,
      value
    ]
  )

  const checkout = useCallback(async () => {
    setSubmitting(true)

    completeGiftCardPayment(
      { email: getValues()['senderEmail'] },
      onPaymentCompleted,
      () => {
        setSubmitting(false)
      },
      () => {
        alertError(t('gift-card-purchase-form.payment-failed'))
        setSubmitting(false)
      }
    )
  }, [completeGiftCardPayment, onPaymentCompleted, getValues])

  if (complete) {
    return (
      <div
        className='giftCardPurchaseForm'
        data-testid='giftCardPurchaseSuccess'
      >
        <Image src='icons/checkmark-success.svg' alt='' />
        <p className='successMessage'>
          {t('gift-card-purchase-form.gift-card-successfully-sent')}
        </p>
      </div>
    )
  }

  if (isIntlRestaurant) {
    return (
      <div
        className='giftCardPurchaseForm'
        data-testid='giftCardPurchaseAdyenForm'
      >
        <AdyenGiftCardPaymentIFrame
          checkAmount={parseFloat(getValues()['amount'])}
          email={getValues()['senderEmail']}
          onPaymentSubmitted={() => setSubmitting(true)}
          onPaymentFailed={() => setSubmitting(false)}
          onPaymentCompleted={onPaymentCompleted}
        />
      </div>
    )
  }

  return (
    <div className='giftCardPurchaseForm' data-testid='giftCardPurchaseCCForm'>
      <CreditCardFormSPI anchorClassName='ccForm' />
      <BrandedButton
        className='button'
        variant={ButtonType.Primary}
        disabled={!isPaymentValid || submitting}
        onClick={checkout}
      >
        {t('gift-card-purchase-form.purchase')}
      </BrandedButton>
    </div>
  )
}

export const GiftCardPurchaseForm = ({
  maxPurchaseAmount,
  minPurchaseAmount
}: Props) => {
  const { isEditor } = useEditor()
  const formMethods = useFormContext()
  const [isCheckingOut, setIsCheckingOut] = useState(false)
  const [paymentIntentError, setPaymentIntentError] = useState(false)
  const { customer } = useCustomer()

  const ooClient = useOOClient()

  const currencySymbol = useCurrencySymbol()

  const handlePaymentIntentError = useCallback(() => {
    setPaymentIntentError(true)
    setIsCheckingOut(false)
  }, [])

  if (isCheckingOut) {
    return (
      <ApolloProvider client={ooClient}>
        <CartContextProvider>
          <PaymentContextProvider
            useGiftCardFlow
            giftCardAmount={parseFloat(formMethods.getValues()['amount'])}
          >
            <GiftCardPayment onPaymentIntentError={handlePaymentIntentError} />
          </PaymentContextProvider>
        </CartContextProvider>
      </ApolloProvider>
    )
  }

  // Clamp min and max values between the ones enforced by the backend fraud limits
  const effectiveMaxValue = Math.min(
    maxPurchaseAmount || DEFAULT_MAX_PURCHASE_AMOUNT,
    DEFAULT_MAX_PURCHASE_AMOUNT
  )
  const effectiveMinValue = Math.max(
    minPurchaseAmount || DEFAULT_MIN_PURCHASE_AMOUNT,
    DEFAULT_MIN_PURCHASE_AMOUNT
  )

  return (
    <div className='giftCardPurchaseForm' data-testid='giftCardPurchaseForm'>
      <div className='row'>
        <InputField
          disabled={isEditor}
          id='amount'
          label={t('gift-card-purchase-form.gift-amount', {
            currencySymbol,
            effectiveMinValue,
            effectiveMaxValue
          })}
          required
          type='number'
          inputMode='decimal'
          pattern='\d{1,3}(?:\.\d{1,2})?$'
          prefix={currencySymbol}
          validate={(value) =>
            AMOUNT_REGEX.test(value) &&
            parseFloat(value) >= effectiveMinValue &&
            parseFloat(value) <= effectiveMaxValue
          }
        />
      </div>
      <div className='row'>
        <InputField
          disabled={isEditor}
          id='recipientName'
          label={t('gift-card-purchase-form.who-is-this-gift-for')}
        />
        <InputField
          disabled={isEditor}
          id='recipientEmail'
          required
          type='email'
          label={t('gift-card-purchase-form.recipient-email')}
          validate={(e) =>
            isEmail(e) ||
            t('gift-card-purchase-form.please-enter-valid-email-address')
          }
        />
      </div>
      <div className='row'>
        <InputField
          disabled={isEditor}
          id='senderName'
          defaultValue={
            customer
              ? (customer?.firstName || '') + ' ' + (customer?.lastName || '')
              : undefined
          }
          label={t('gift-card-purchase-form.who-is-this-gift-from')}
        />
        <InputField
          disabled={isEditor}
          id='senderEmail'
          required
          type='email'
          defaultValue={customer?.email}
          label={t('gift-card-purchase-form.sender-email')}
          validate={(e) =>
            isEmail(e) ||
            t('gift-card-purchase-form.please-enter-valid-email-address')
          }
        />
      </div>
      <div className='row'>
        <TextArea
          disabled={isEditor}
          id='message'
          label={t('gift-card-purchase-form.add-custom-message-125-chars-max')}
          maxLength={125}
        />
      </div>
      {paymentIntentError && (
        <div className='errorMessage' role='alert'>
          {t('gift-card-purchase-form.payment-intent-error')}
        </div>
      )}
      <BrandedButton
        type='button'
        className='button'
        variant={ButtonType.Primary}
        disabled={
          paymentIntentError || !formMethods.formState.isValid || isEditor
        }
        onClick={() => {
          setIsCheckingOut(true)
        }}
      >
        {t('gift-card-purchase-form.continue-to-payment')}
      </BrandedButton>
    </div>
  )
}
